// Form draft auto-recovery.
//
// Long data entries (sales order, MRN, ...) live only in the DOM until the
// user clicks Save. A freeze, closed tab, dead network or a failed save API
// call destroys up to 30+ minutes of work. This module keeps a rolling backup
// of the entry in localStorage on the user's machine (works offline, survives
// crashes) and offers to restore it when the form is opened again.
//
// Both capture and restore are generic and run on every SSR dataform: capture
// reads named fields + AG-Grid rows, restore writes them back. Forms with
// structural toggles that reshape the DOM (e.g. sales order's Lumpsum/Product
// switch) register a per-form hook in FORM_QUIRKS so restore reproduces that
// shape before pushing values in. "View data" is always available as a fallback
// when in-place restore can't fully round-trip a form.
import { showFlashMessage } from './flashmessages.js';

const DRAFT_PREFIX = 'formdraft:';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const SNAPSHOT_INTERVAL_MS = 30 * 1000;
// Save/Submit endpoints share the /dataform/submit* path family on all forms.
const SAVE_PATH_RE = /\/ssr\/([^/]+)\/dataform\/submit(entry|order)?(\?|$)/;

// Forms opted into auto-recovery. The slug lists live in config/prod.py and are
// handed to the client on the body element by base.html, so server/client stay
// in sync without duplicating the lists here.
let enabledSlugsCache = null;
let disabledSlugsCache = null;
function slugSetFromBodyData(name) {
    const raw = (document.body && document.body.dataset[name]) || '';
    return new Set(raw.split(',').map((s) => s.trim()).filter(Boolean));
}
function enabledSlugs() {
    if (enabledSlugsCache) return enabledSlugsCache;
    enabledSlugsCache = slugSetFromBodyData('formdraftEnabledSlugs');
    return enabledSlugsCache;
}
function disabledSlugs() {
    if (disabledSlugsCache) return disabledSlugsCache;
    disabledSlugsCache = slugSetFromBodyData('formdraftDisabledSlugs');
    return disabledSlugsCache;
}
export function isFormdraftSlugAllowed(slug, enabled, disabled) {
    return enabled.has(slug) && !disabled.has(slug);
}
// tell_user("success", ...) renders exactly this call in flash_messages.html.
// Its presence in a save response is the only reliable "really saved" signal,
// since validation failures also come back as HTTP 200.
const SUCCESS_FLASH_RE = /showFlashMessage\('[^']*',\s*'success'/;

let mgr = null; // active dataform context: {slug, scope, recordId, key, instanceId}
let dirty = false;
let suppressDirtyUntil = 0;
let lastUserInteraction = 0;
let snapshotTimer = null;
let pendingSaveKey = null;
let lastNetToastAt = 0;
const restoredKeys = new Set();
const dismissedKeys = new Set();

function nowMs() {
    return Date.now();
}

function newFormInstanceId() {
    return `${nowMs()}:${Math.random().toString(36).slice(2)}`;
}

function userScopePrefix() {
    const body = document.body || {};
    const ds = body.dataset || {};
    const uid = ds.userAccountId || 'anon';
    const ccode = ds.ccode || 'c';
    return `${DRAFT_PREFIX}${ccode}:${uid}:`;
}

function draftKey(slug, recordId) {
    return `${userScopePrefix()}${slug}:${recordId}`;
}

function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// btoa() throws on non-latin text (item names in Hindi etc.), same guard as grid.js.
function safeBtoa(text) {
    try {
        return btoa(unescape(encodeURIComponent(text)));
    } catch (_e) {
        return '';
    }
}

function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\\]]/g, '\\$&');
}

// ---------------------------------------------------------------------------
// Human-readable presentation helpers (for the banner + View data fallback)
// ---------------------------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, '0');

// The app's users read dates as dd/mm/yyyy; JS toLocaleString defaults to the
// browser locale (often mm/dd). Format explicitly.
function formatTimestamp(ms) {
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Reformat ISO date/datetime values (yyyy-mm-dd...) to dd/mm/yyyy for display
// only — the stored value is left untouched so restore round-trips exactly.
// Keeps the time portion for datetime values so it isn't silently dropped.
function formatMaybeDate(value) {
    if (typeof value !== 'string') return value;
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]([0-9:]{4,8}))?$/);
    if (!m) return value;
    return m[4] ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}` : `${m[3]}/${m[2]}/${m[1]}`;
}

function cleanLabel(text) {
    return String(text || '').replace(/[*:]/g, '').replace(/\s+/g, ' ').trim();
}

// Find the human label a user sees for a field, so View data reads like the
// form ("Vendor: ...") instead of raw names, and so technical/hidden fields
// (slug, leadmaster_id, period, ...) — which have no label — are skipped.
// The label's own text only. Ignore help controls from older/custom field
// layouts so their accessible label never leaks into the draft summary.
function labelOwnText(labelEl) {
    const clone = labelEl.cloneNode(true);
    clone.querySelectorAll('.smaac-tooltip-trigger, [data-smaac-tooltip], [role="tooltip"], svg').forEach((n) => n.remove());
    return cleanLabel(clone.textContent);
}

function getFieldLabel(name) {
    const direct = document.querySelector(`label[for="${cssEscape(name)}"]`);
    if (direct) return labelOwnText(direct);
    const ctrl = document.querySelector(`[name="${cssEscape(name)}"]`) || document.getElementById(name);
    if (ctrl) {
        const wrap = ctrl.closest('.relative, .field, .form-group, [class*="flex"], [class*="grid"]');
        const within = wrap && wrap.querySelector('label');
        if (within) return labelOwnText(within);
        const aria = ctrl.getAttribute && ctrl.getAttribute('aria-label');
        if (aria) return cleanLabel(aria);
    }
    return '';
}

// Best-effort proper form name ("Purchase Order"), generic across all forms:
// page_title is set server-side from the nav label for every path and surfaces
// as document.title ("<Name> - Smaac"). Falls back to a prettified slug.
function resolveFormName(slug) {
    const title = (document.title || '').replace(/\s*[-|]\s*Smaac\s*$/i, '').trim();
    if (title && title.toLowerCase() !== 'smaac') return title;
    return String(slug || '')
        .replace(/ssr$|dataform$/i, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase()) || slug;
}

// Names that are plumbing, not user data — never count toward "has data" and
// never shown in View data. Covers the hidden fields every dataform carries
// (period/showview/etc.) plus foreign-key ids and internal list inputs.
const TECHNICAL_NAMES = new Set([
    'id', 'showview', 'listeddetails', 'period', 'start_date', 'end_date',
    'report', 'apitoken', 'slug', 'ctype', 'gridtemplate', 'fliplinetabid',
]);
function isTechnicalName(name) {
    const n = String(name || '').toLowerCase();
    if (TECHNICAL_NAMES.has(n)) return true;
    if (/(^|_)id$/.test(n)) return true; // foreign-key ids (leadmaster_id, ...)
    if (/_input$/.test(n)) return true; // VirtualSelect mirror inputs
    if (/^listed/.test(n)) return true; // listedDetails / listedDeleted / ...
    return false;
}

// What a name shows as in View data: its label, else its raw name if it's a
// real (non-technical) field, else nothing. Shared so capture, the banner and
// View data all agree on "is there anything to show?".
function displayNameFor(name, labels) {
    if (labels && labels[name]) return labels[name];
    // v2: labels is the on-screen-label contract — a field with no captured
    // label is not something the user sees, so don't expose it (prevents hidden
    // app-specific fields from showing as raw names / counting as displayable).
    if (labels) return '';
    // v1 (legacy drafts, no labels captured): best-effort name minus technical.
    return isTechnicalName(name) ? '' : name;
}

// True only if the draft holds something the user would recognise as their
// entry. Prevents the banner from offering a "restore" whose View data is empty
// (e.g. a form where only hidden defaults like period/slug were non-blank).
function draftHasDisplayableData(draft) {
    if (!draft) return false;
    const grids = draft.grids || {};
    if (Object.values(grids).some((rows) => Array.isArray(rows) && rows.length)) return true;
    const labels = draft.labels;
    const shows = (name) => !!displayNameFor(name, labels);
    if ((draft.fields || []).some((f) => shows(f.n) && (f.t === 'c' ? f.v : (f.v !== '' && f.v != null)))) return true;
    if (Object.entries(draft.radios || {}).some(([n, v]) => v != null && shows(n))) return true;
    if (Object.entries(draft.vs || {}).some(([n, v]) => v !== '' && v != null && shows(n))) return true;
    return false;
}

// A readable label for a grid section from its ag-id (e.g.
// "ssr-purchaseorderssr-details" -> "Details", "...-master" -> "Items").
function gridDisplayLabel(agId, slug) {
    let tail = String(agId || '')
        .replace(/^ssr-?/i, '')
        .replace(new RegExp(slug, 'ig'), '')
        .replace(/grid|container/ig, '')
        .replace(/[_-]+/g, ' ')
        .trim();
    if (/^master$/i.test(tail) || !tail) tail = 'Items';
    return tail.replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function loadDraft(key) {
    let raw = null;
    try {
        raw = localStorage.getItem(key);
    } catch (_e) {
        return null;
    }
    if (!raw) return null;
    try {
        const draft = JSON.parse(raw);
        if (!draft || !draft.savedAt || nowMs() - draft.savedAt > DRAFT_TTL_MS) {
            localStorage.removeItem(key);
            return null;
        }
        return draft;
    } catch (_e) {
        localStorage.removeItem(key);
        return null;
    }
}

function removeDraft(key) {
    try {
        localStorage.removeItem(key);
    } catch (_e) { /* storage unavailable: nothing to clean */ }
}

function pruneDrafts(removeAllOthers, keepKey) {
    const stale = [];
    // The aggressive (quota) pass must stay within the current account's scope:
    // keys are partitioned by ccode:uid, and wiping another account's drafts on
    // this shared browser would be silent data loss for them.
    const scope = userScopePrefix();
    try {
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith(DRAFT_PREFIX) || key === keepKey) continue;
            if (removeAllOthers) {
                if (key.startsWith(scope)) stale.push(key);
                continue;
            }
            try {
                const draft = JSON.parse(localStorage.getItem(key));
                if (!draft || !draft.savedAt || nowMs() - draft.savedAt > DRAFT_TTL_MS) {
                    stale.push(key);
                }
            } catch (_e) {
                stale.push(key);
            }
        }
        stale.forEach((key) => localStorage.removeItem(key));
    } catch (_e) { /* storage unavailable */ }
}

function persistDraft(key, draft) {
    const payload = JSON.stringify(draft);
    try {
        localStorage.setItem(key, payload);
    } catch (_e) {
        // Quota exceeded: old drafts are worth less than the one being typed now.
        pruneDrafts(true, key);
        try {
            localStorage.setItem(key, payload);
        } catch (err) {
            console.warn('[formdraft] could not persist draft:', err);
        }
    }
}

// ---------------------------------------------------------------------------
// Snapshot (capture)
// ---------------------------------------------------------------------------

function currentRecordId(slug, scope) {
    // Look inside the form scope only: a document-wide [name="id"] could match a
    // listing row or another widget and mis-key the draft.
    const root = scope || document;
    const el = root.querySelector(`[name="id"][data-formname="${cssEscape(slug)}"]`)
        || root.querySelector('[name="id"]');
    const value = el && el.value != null ? String(el.value).trim() : '';
    return value && value !== '0' ? value : 'new';
}

function captureFields(slug) {
    const fields = [];
    const radios = {};
    const seenValueNames = {};
    const elements = document.querySelectorAll(`[name][data-formname="${slug}"]`);
    elements.forEach((el) => {
        const name = el.getAttribute('name');
        if (!name || name === 'apitoken') return;
        const type = (el.type || '').toLowerCase();
        if (type === 'file' || type === 'password') return;
        if (type === 'radio') {
            if (el.checked) radios[name] = el.value;
            else if (!(name in radios)) radios[name] = null;
            return;
        }
        if (type === 'checkbox') {
            fields.push({ n: name, t: 'c', v: !!el.checked });
            return;
        }
        const idx = seenValueNames[name] || 0;
        seenValueNames[name] = idx + 1;
        // Masked fields (data-masked-placeholder) are sensitive (shown as "***").
        // Do NOT write their real value to localStorage by default — that would
        // defeat at-rest masking for PII/secrets. A field can opt in with
        // data-formdraft-persist-masked="1" (then it's restored and shown masked
        // via m:1). Otherwise skip it entirely.
        if (el.dataset && el.dataset.maskedPlaceholder === '1') {
            if (el.dataset.formdraftPersistMasked !== '1') return;
            const real = el.dataset.maskedRealValue != null ? el.dataset.maskedRealValue : '';
            fields.push({ n: name, t: 'v', i: idx, v: real, m: 1 });
            return;
        }
        // Some names repeat (e.g. MRN renders several hidden "mrndate" inputs);
        // keep an index so restore writes them back in document order.
        fields.push({ n: name, t: 'v', i: idx, v: el.value == null ? '' : String(el.value) });
    });
    return { fields, radios };
}

function captureVirtualSelects(slug) {
    // VirtualSelect widgets keep their displayed selection on the widget node
    // (id == field name); the plain [name] inputs alone don't redraw the label.
    // Also capture the displayed label text so View data is human-readable and
    // restore can seed a label for deferred-option widgets (which render blank).
    const values = {};
    const texts = {};
    document.querySelectorAll(`[name][data-formname="${slug}"]`).forEach((el) => {
        const name = el.getAttribute('name');
        if (!name || values[name] !== undefined) return;
        const widget = document.getElementById(name);
        if (widget && widget.dataset && widget.dataset.init === 'virtual-select') {
            const value = widget.value != null && widget.value !== '' ? widget.value : el.value;
            values[name] = value == null ? '' : value;
            const shown = widget.querySelector && widget.querySelector('.vscomp-value');
            const text = shown ? shown.textContent : '';
            if (text && !/^select\b/i.test(text.trim())) texts[name] = text.trim();
        }
    });
    return { values, texts };
}

function captureGrids(scope) {
    const grids = {};
    // scope is the entry-form subtree (see findFormScope), so listing/report
    // grids that live in sibling subtrees are not captured.
    (scope || document).querySelectorAll('[data-ag-id]').forEach((el) => {
        const agId = el.dataset.agId;
        const api = agId ? window[`aggridApi:${agId}`] : null;
        if (!api || (api.isDestroyed && api.isDestroyed())) return;
        const rows = [];
        try {
            api.forEachNode((node) => {
                if (node && node.data && !node.rowPinned) rows.push(node.data);
            });
        } catch (_e) {
            return;
        }
        grids[agId] = rows;
    });
    return grids;
}

// Column field->header map per grid, so View data shows readable headers and
// only the columns the user actually sees (not internal/hidden grid fields).
function captureGridMeta(scope, slug) {
    const meta = {};
    (scope || document).querySelectorAll('[data-ag-id]').forEach((el) => {
        const agId = el.dataset.agId;
        const api = agId ? window[`aggridApi:${agId}`] : null;
        if (!api || (api.isDestroyed && api.isDestroyed())) return;
        let cols = [];
        try {
            // Flatten one level of column groups (defs with `children`).
            const flat = [];
            (api.getColumnDefs ? api.getColumnDefs() : []).forEach((d) => {
                if (d && Array.isArray(d.children)) flat.push(...d.children);
                else if (d) flat.push(d);
            });
            cols = flat
                .filter((d) => d && d.field && !d.hide && d.headerName !== '')
                .map((d) => ({ f: d.field, h: cleanLabel(d.headerName != null ? d.headerName : d.field) }))
                .slice(0, 30);
        } catch (_e) { /* keep cols empty -> View data falls back to raw keys */ }
        meta[agId] = { label: gridDisplayLabel(agId, slug), cols };
    });
    return meta;
}

// Build the current form's draft payload without persisting. Shared by
// captureNow and the banner's "is this draft just the loaded data?" check.
function buildSnapshot() {
    if (!mgr) return null;
    const { fields, radios } = captureFields(mgr.slug);
    const grids = captureGrids(mgr.scope);
    const { values: vs, texts: vsText } = captureVirtualSelects(mgr.slug);
    // Human labels for the captured names, so View data reads like the form.
    // Absent label => technical/hidden field => View data will skip it.
    const labels = {};
    const addLabel = (name) => {
        if (name && labels[name] === undefined) {
            const label = getFieldLabel(name);
            if (label) labels[name] = label;
        }
    };
    fields.forEach((f) => addLabel(f.n));
    Object.keys(radios).forEach(addLabel);
    Object.keys(vs).forEach(addLabel);
    const rowCount = Object.values(grids).reduce((sum, rows) => sum + rows.length, 0);
    return { fields, radios, vs, vsText, labels, grids, rowCount };
}

// Stable signature of a draft's USER-FACING content, used to tell whether a
// stored draft actually differs from what's on screen. Lets us suppress the
// recovery banner when the draft just mirrors an already-loaded saved record
// (e.g. opening or cancelling a saved voucher) — there is nothing to recover.
function draftSignature(d) {
    if (!d) return '';
    const labels = d.labels;
    const norm = (v) => String(v == null ? '' : v).trim();
    const fields = (d.fields || [])
        .filter((f) => displayNameFor(f.n, labels))
        .map((f) => `${f.n}#${f.i || 0}=${f.t === 'c' ? (f.v ? 1 : 0) : norm(f.v)}`)
        .sort();
    const vs = Object.entries(d.vs || {})
        .filter(([n, v]) => v !== '' && v != null && displayNameFor(n, labels))
        .map(([n, v]) => `${n}=${norm(v)}`).sort();
    const radios = Object.entries(d.radios || {})
        .filter(([n, v]) => v != null && displayNameFor(n, labels))
        .map(([n, v]) => `${n}=${norm(v)}`).sort();
    const grids = Object.entries(d.grids || {})
        .map(([k, rows]) => `${k}=${JSON.stringify(rows)}`).sort();
    return JSON.stringify({ fields, vs, radios, grids });
}

function captureNow() {
    if (!mgr) return;
    try {
        const snap = buildSnapshot();
        if (!snap) return;
        // Don't persist a draft with nothing the user would recognise as their
        // data (e.g. only hidden defaults like period/slug were non-blank). Same
        // predicate the banner/View data use; grid rows always count.
        if (!draftHasDisplayableData(snap)) return;
        persistDraft(mgr.key, {
            v: 2,
            slug: mgr.slug,
            formName: resolveFormName(mgr.slug),
            recordId: mgr.recordId,
            formInstanceId: mgr.instanceId,
            savedAt: nowMs(),
            page: window.location.pathname + window.location.search,
            fields: snap.fields,
            radios: snap.radios,
            vs: snap.vs,
            vsText: snap.vsText,
            labels: snap.labels,
            grids: snap.grids,
            gridMeta: captureGridMeta(mgr.scope, mgr.slug),
            rowCount: snap.rowCount,
        });
    } catch (err) {
        console.warn('[formdraft] snapshot failed:', err);
    }
}

function flushIfDirty() {
    if (!mgr || !dirty) return;
    dirty = false;
    captureNow();
}

export function shouldSkipRecoveryDraft(draft, context, currentTime = nowMs()) {
    if (!draft) return true;
    const ageMs = currentTime - draft.savedAt;
    if (!draft.formInstanceId && ageMs >= 0 && ageMs < 5000) return true;
    return !!(draft.formInstanceId && context && draft.formInstanceId === context.instanceId);
}

// ---------------------------------------------------------------------------
// Restore
// ---------------------------------------------------------------------------

function setVirtualSelectValue(name, value, text) {
    const widget = document.getElementById(name);
    const hidden = document.getElementById(`${name}_input`);
    const normalized = value == null ? '' : String(value);
    if (widget && widget.dataset && widget.dataset.init === 'virtual-select') {
        // Deferred-options widgets (e.g. Purchase Order vendor/item/template)
        // render with no options, so setValue can't find a label and the toggle
        // stays blank. Seed a one-off option for the captured value/label first.
        if (normalized && text) {
            try {
                const opts = Array.isArray(widget.options) ? widget.options : [];
                if (!opts.some((o) => String(o && o.value) === normalized) && typeof widget.setOptions === 'function') {
                    widget.setOptions(opts.concat([{ label: text, value: normalized }]));
                }
            } catch (_e) { /* fall through; hidden value below still posts */ }
        }
        if (typeof widget.setValueSilently === 'function') {
            widget.setValueSilently(normalized);
        } else if (typeof widget.setValue === 'function') {
            widget.setValue(normalized);
        } else {
            widget.value = normalized;
        }
        // Last resort: if the toggle still shows the placeholder, paint the label
        // so the user can see the restored selection.
        if (text) {
            const shown = widget.querySelector && widget.querySelector('.vscomp-value');
            if (shown && (!shown.textContent || /^select\b/i.test(shown.textContent.trim()))) {
                shown.textContent = text;
            }
        }
    }
    if (hidden) hidden.value = normalized;
}

function restoreFields(draft) {
    const slug = draft.slug;
    const valueElsByName = {};
    document.querySelectorAll(`[name][data-formname="${slug}"]`).forEach((el) => {
        const name = el.getAttribute('name');
        const type = (el.type || '').toLowerCase();
        if (!name || type === 'file' || type === 'radio' || type === 'checkbox') return;
        (valueElsByName[name] = valueElsByName[name] || []).push(el);
    });
    (draft.fields || []).forEach((field) => {
        if (field.t === 'c') {
            document.querySelectorAll(`[name="${cssEscape(field.n)}"][data-formname="${slug}"]`)
                .forEach((el) => {
                    if ((el.type || '').toLowerCase() === 'checkbox') el.checked = !!field.v;
                });
            return;
        }
        const els = valueElsByName[field.n] || [];
        const el = els[field.i || 0];
        if (!el) return;
        if (field.m && el.dataset) {
            // Masked field: keep the real value where the form reads it on submit,
            // but leave the visible input masked.
            el.dataset.maskedRealValue = field.v;
            el.value = el.dataset.maskedPlaceholderValue || '***';
        } else {
            el.value = field.v;
        }
    });
    Object.entries(draft.radios || {}).forEach(([name, value]) => {
        if (value == null) return;
        // Scope to this dataform: generic names like "type" also exist on
        // unrelated filters/modals on the same page and must not be toggled.
        document.querySelectorAll(`input[type="radio"][name="${cssEscape(name)}"][data-formname="${slug}"]`).forEach((el) => {
            el.checked = String(el.value) === String(value);
        });
    });
    const vsText = draft.vsText || {};
    Object.entries(draft.vs || {}).forEach(([name, value]) => setVirtualSelectValue(name, value, vsText[name]));
    repaintRestoredSelects(draft);
}

// Native Preline advanced-selects show "Choose" until re-synced after a
// programmatic value change. Best-effort: ask Preline to re-read the value.
// The native <select> value is already set, so the form submits correctly even
// if this repaint is unavailable.
function repaintRestoredSelects(draft) {
    (draft.fields || []).forEach((f) => {
        if (f.t !== 'v') return;
        const el = document.querySelector(`select[name="${cssEscape(f.n)}"][data-formname="${cssEscape(draft.slug)}"]`);
        if (!el || !el.hasAttribute('data-hs-select')) return;
        try {
            const inst = window.HSSelect && typeof window.HSSelect.getInstance === 'function'
                ? window.HSSelect.getInstance(el, true)
                : null;
            if (inst && typeof inst.setValue === 'function') inst.setValue(el.value);
        } catch (_e) { /* native value already set; submit is correct regardless */ }
    });
}

function restoreGrids(draft) {
    Object.entries(draft.grids || {}).forEach(([agId, rows]) => {
        const api = window[`aggridApi:${agId}`];
        if (api && !(api.isDestroyed && api.isDestroyed())) {
            api.setGridOption('rowData', rows);
        }
        // Keep the DOM-side row store in sync: page scripts (gridRowsJson,
        // copy/print flows) read rows from here, not from the grid api.
        const gridEl = document.querySelector(`[data-ag-id="${cssEscape(agId)}"]`);
        if (gridEl) {
            const json = JSON.stringify(rows);
            const scriptId = gridEl.dataset.agInitialRowsScript;
            if (scriptId) {
                const script = document.getElementById(scriptId);
                if (script) script.textContent = json;
            }
            gridEl.dataset.agInitialRows = safeBtoa(json);
        }
    });
}

function waitForHtmxSettle(timeoutMs) {
    return new Promise((resolve) => {
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            document.removeEventListener('htmx:afterSettle', finish);
            resolve();
        };
        document.addEventListener('htmx:afterSettle', finish);
        setTimeout(finish, timeoutMs);
    });
}

// Structural quirks that must be settled BEFORE field/grid values are pushed in.
const FORM_QUIRKS = {
    salesdataform: {
        // The Lumpsum/Product toggle swaps the master grid markup server-side;
        // restoring Product rows into a Lumpsum grid would corrupt the entry.
        async beforeRestore(draft) {
            const wanted = draft.radios && draft.radios.type;
            if (!wanted) return;
            const container = document.getElementById('type');
            const current = container && container.dataset ? container.dataset.checked : null;
            if (!current || current === wanted) return;
            const radio = container.querySelector(`input[type="radio"][value="${cssEscape(wanted)}"]`);
            if (!radio) return;
            radio.click();
            await waitForHtmxSettle(4000);
        },
    },
};

async function restoreDraft(draft) {
    suppressDirtyUntil = nowMs() + 3000;
    const quirk = FORM_QUIRKS[draft.slug];
    if (quirk && quirk.beforeRestore) await quirk.beforeRestore(draft);
    restoreFields(draft);
    restoreGrids(draft);
    suppressDirtyUntil = nowMs() + 1500;
    document.dispatchEvent(new CustomEvent('formdraft:restored', { detail: { slug: draft.slug } }));
}

// ---------------------------------------------------------------------------
// Banner + view modal
// ---------------------------------------------------------------------------

function draftSummary(draft) {
    const when = formatTimestamp(draft.savedAt);
    const rows = draft.rowCount ? ` (${draft.rowCount} item row${draft.rowCount === 1 ? '' : 's'})` : '';
    return `Unsaved entry from ${when}${rows} was recovered on this device.`;
}

function removeBanner() {
    const banner = document.getElementById('formdraft-banner');
    if (banner) banner.remove();
}

function viewCell(value, extra) {
    return `<td class="border border-slate-200 px-2 py-1 ${extra || ''}">${escapeHtml(formatMaybeDate(value))}</td>`;
}

function showViewModal(draft) {
    const existing = document.getElementById('formdraft-view-modal');
    if (existing) existing.remove();
    const labels = draft.labels;
    // Show a field's label, else its raw name if it's a real (non-technical)
    // field, else hide it. Same rule capture/banner use (displayNameFor), so the
    // view is never empty when the banner is shown.
    const displayName = (name) => displayNameFor(name, labels);

    const fieldRows = (draft.fields || [])
        .filter((f) => displayName(f.n) && (f.t !== 'v' || (f.v !== '' && f.v != null)))
        .map((f) => {
            // Masked fields stay masked in the view even though the real value
            // is captured for restore.
            const value = f.m ? '***' : (f.t === 'c' ? (f.v ? 'Yes' : 'No') : f.v);
            return `<tr><td class="border border-slate-200 px-2 py-1 font-medium text-slate-600">${escapeHtml(displayName(f.n))}</td>`
                + viewCell(value) + '</tr>';
        });
    const radioRows = Object.entries(draft.radios || {})
        .filter(([name, value]) => value != null && displayName(name))
        .map(([name, value]) => `<tr><td class="border border-slate-200 px-2 py-1 font-medium text-slate-600">${escapeHtml(displayName(name))}</td>`
            + viewCell(value) + '</tr>');
    // Dropdown (VirtualSelect) selections live in draft.vs, not draft.fields, and
    // were missing from View data entirely. Show the human label the user picked
    // (draft.vsText) rather than the raw id.
    const vsTextMap = draft.vsText || {};
    const vsRows = Object.entries(draft.vs || {})
        .filter(([name, value]) => value !== '' && value != null && displayName(name))
        .map(([name, value]) => `<tr><td class="border border-slate-200 px-2 py-1 font-medium text-slate-600">${escapeHtml(displayName(name))}</td>`
            + viewCell(vsTextMap[name] || value) + '</tr>');
    const allFieldRows = fieldRows.concat(vsRows).concat(radioRows).join('');

    const gridTables = Object.entries(draft.grids || {})
        .filter(([, rows]) => rows.length)
        .map(([agId, rows]) => {
            const meta = (draft.gridMeta && draft.gridMeta[agId]) || {};
            // Prefer captured visible columns; fall back to data keys for v1 drafts.
            let cols = (meta.cols && meta.cols.length)
                ? meta.cols
                : Object.keys(rows[0]).filter((c) => !c.startsWith('_') && !c.includes('.')).slice(0, 10)
                    .map((c) => ({ f: c, h: c }));
            const head = cols.map((c) => `<th class="border border-slate-200 px-2 py-1 text-left font-semibold text-slate-600">${escapeHtml(c.h)}</th>`).join('');
            const CAP = 200;
            const body = rows.slice(0, CAP).map((row) => `<tr>${
                cols.map((c) => viewCell(row[c.f])).join('')
            }</tr>`).join('');
            const title = `${meta.label || gridDisplayLabel(agId, draft.slug)} — ${rows.length} row${rows.length === 1 ? '' : 's'}`;
            // All rows are captured/restored; the view just caps rendering — say so
            // so the user doesn't think rows beyond the cap were lost.
            const capNote = rows.length > CAP
                ? `<p class="mt-1 text-[11px] text-slate-500">Showing first ${CAP} of ${rows.length} rows (all ${rows.length} are restored).</p>`
                : '';
            return `<h4 class="mt-4 mb-1 text-sm font-semibold text-slate-700">${escapeHtml(title)}</h4>`
                + `<div class="overflow-x-auto"><table class="text-xs border-collapse"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>${capNote}`;
        })
        .join('');

    const body = (allFieldRows || gridTables)
        ? (allFieldRows ? `<table class="text-xs border-collapse w-full"><tbody>${allFieldRows}</tbody></table>` : '') + gridTables
        : '<p class="text-sm text-slate-500">No readable data was captured.</p>';

    const modal = document.createElement('div');
    modal.id = 'formdraft-view-modal';
    modal.className = 'fixed inset-0 z-[2147483000] flex items-center justify-center bg-slate-900/50 px-2 sm:px-4';
    modal.innerHTML = '<div class="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">'
        + '<div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">'
        + `<h3 class="text-base font-semibold text-slate-900">Recovered data — ${escapeHtml(draft.formName || resolveFormName(draft.slug))}</h3>`
        + '<button type="button" data-formdraft-close class="rounded px-2 py-1 text-base font-semibold text-slate-600 hover:bg-slate-100">✖</button>'
        + '</div>'
        + `<div class="overflow-auto px-4 py-3">${body}</div></div>`;
    modal.addEventListener('click', (evt) => {
        if (evt.target === modal || (evt.target.closest && evt.target.closest('[data-formdraft-close]'))) {
            modal.remove();
        }
    });
    document.body.appendChild(modal);
}

// Touch-friendly action button (h-9 keeps a 36px tap target on mobile, where
// the previous text-xs buttons were hard to hit).
function bannerButton(action, text, classes) {
    return `<button type="button" data-formdraft-${action} class="inline-flex h-9 items-center justify-center rounded-md px-3 text-xs font-semibold ${classes}">${text}</button>`;
}

function renderBannerActions(banner) {
    banner.querySelector('[data-formdraft-actions]').innerHTML =
        bannerButton('restore', 'Restore entry', 'bg-amber-600 text-white hover:bg-amber-700')
        + bannerButton('view', 'View data', 'border border-amber-400 bg-white text-amber-800 hover:bg-amber-100')
        + bannerButton('discard', 'Discard', 'border border-amber-400 bg-white text-amber-800 hover:bg-amber-100');
}

// Inline (non-modal) discard confirmation, per request to avoid popup modals
// and to guard against accidental taps.
function renderDiscardConfirm(banner) {
    banner.querySelector('[data-formdraft-actions]').innerHTML =
        '<span class="mr-1 text-xs font-medium text-amber-900">Discard recovered data?</span>'
        + bannerButton('discard-yes', 'Yes, discard', 'bg-red-600 text-white hover:bg-red-700')
        + bannerButton('discard-no', 'Keep', 'border border-amber-400 bg-white text-amber-800 hover:bg-amber-100');
}

function maybeShowBanner() {
    if (!mgr) return;
    const key = mgr.key;
    if (restoredKeys.has(key) || dismissedKeys.has(key)) return;

    const existing = document.getElementById('formdraft-banner');
    if (existing) {
        // Same record: leave it (don't tear out an in-progress discard confirm).
        // Different record (the id field drifted in place, e.g. user picked a
        // saved voucher): the old banner is bound to the wrong record and would
        // restore the wrong draft, so drop it and rebuild for the current key.
        if (existing.dataset.draftKey === key) return;
        existing.remove();
    }

    const draft = loadDraft(key);
    // A draft from this still-active form instance is the live backup, not recovery.
    if (shouldSkipRecoveryDraft(draft, mgr)) return;
    // Never offer a restore with nothing to show (the View-data-is-empty bug):
    // a draft whose only non-blank values are hidden/technical fields. Also
    // cleans up such junk drafts left by older builds.
    if (!draftHasDisplayableData(draft)) {
        removeDraft(key);
        return;
    }
    // Don't offer a restore that matches what's already on screen. Opening or
    // cancelling an already-saved voucher loads the saved data; a draft equal to
    // it is nothing to recover, so drop it and stay silent. A draft that genuinely
    // differs (real unsaved edits) still shows.
    const current = buildSnapshot();
    if (current && draftSignature(current) === draftSignature(draft)) {
        removeDraft(key);
        return;
    }

    const banner = document.createElement('div');
    banner.id = 'formdraft-banner';
    // Bind the banner to the exact key/draft it represents. mgr is mutable and
    // its key can drift on the next htmx scan; acting on a drifted key was why
    // Discard deleted the wrong entry and the bar kept coming back (also on
    // mobile, which re-scans more often).
    banner.dataset.draftKey = key;
    banner.className = 'mx-1 my-1 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900';
    banner.innerHTML = `<span class="font-medium">⚠ ${escapeHtml(draftSummary(draft))}</span>`
        + '<span class="flex flex-wrap items-center gap-2" data-formdraft-actions></span>';
    renderBannerActions(banner);

    banner.addEventListener('click', async (evt) => {
        const target = evt.target;
        if (target.closest('[data-formdraft-view]')) {
            showViewModal(draft);
            return;
        }
        if (target.closest('[data-formdraft-discard]')) {
            renderDiscardConfirm(banner);
            return;
        }
        if (target.closest('[data-formdraft-discard-no]')) {
            renderBannerActions(banner);
            return;
        }
        if (target.closest('[data-formdraft-discard-yes]')) {
            dismissedKeys.add(key); // suppress the banner for this session only
            // Clear the dirty flag so the 30s timer / pagehide flush doesn't
            // immediately re-persist the just-discarded entry (the actual fix for
            // "Discard won't stick"). Genuinely new edits set dirty again and ARE
            // backed up — Discard only hides the banner, it doesn't disable
            // crash-safety for future work.
            dirty = false;
            removeDraft(key);
            removeBanner();
            showFlashMessage('formdraft-discarded', 'info', 'Recovered draft discarded.', 4);
            return;
        }
        if (target.closest('[data-formdraft-restore]')) {
            restoredKeys.add(key);
            removeBanner();
            try {
                await restoreDraft(draft);
                // Re-save immediately so the restored state survives another crash.
                captureNow();
                showFlashMessage('formdraft-restored', 'success',
                    'Entry restored. Please review the data and click Save.', 8);
            } catch (err) {
                showFlashMessage('formdraft-restore-failed', 'error',
                    'Automatic restore failed. Use "View data" on the recovery bar after reloading to copy your entries.', 12);
                restoredKeys.delete(key);
            }
        }
    });
    mgr.scope.prepend(banner);
}

// ---------------------------------------------------------------------------
// Dataform detection + lifecycle
// ---------------------------------------------------------------------------

function findSaveContext() {
    const buttons = document.querySelectorAll('button[data-save-button][hx-post]');
    for (const btn of buttons) {
        const match = SAVE_PATH_RE.exec(btn.getAttribute('hx-post') || '');
        if (!match) continue;
        const slug = match[1];
        const enabled = enabledSlugs();
        if (isFormdraftSlugAllowed(slug, enabled, disabledSlugs())) {
            return { btn, slug };
        }
    }
    return null;
}

// The entry-form subtree that capture/scope should be confined to.
//
// 1. Most dataforms (sales order, MRN, ...) wrap the whole entry form in an
//    element carrying data-formname="slug" — use it. This matches the original
//    behaviour for those forms exactly (no regression).
// 2. Some forms (e.g. Purchase Order) put the Save button in a header bar that
//    is outside any data-formname wrapper, so step 1 finds nothing and the old
//    code fell back to <body> — sweeping the listing/report grid into the
//    backup. For those, walk up to the smallest ancestor that actually contains
//    the form's field controls (which also contains its line-item grids).
function findFormScope(slug, btn) {
    const wrapper = btn.closest(`[data-formname="${cssEscape(slug)}"]`);
    if (wrapper) return wrapper;

    const fieldSel = `input[data-formname="${cssEscape(slug)}"], select[data-formname="${cssEscape(slug)}"], textarea[data-formname="${cssEscape(slug)}"]`;
    let node = btn;
    while (node && node !== document.body) {
        if (node.querySelector && node.querySelector(fieldSel)) return node;
        if (node.matches && node.matches('form')) return node;
        node = node.parentElement;
    }
    return btn.closest('[data-formname]')
        || document.getElementById('ssr-main-content')
        || document.body;
}

function hookGridEdits(scope) {
    (scope || document).querySelectorAll('[data-ag-id]').forEach((el) => {
        const api = el.dataset.agId ? window[`aggridApi:${el.dataset.agId}`] : null;
        if (!api || api.__formdraftHooked || (api.isDestroyed && api.isDestroyed())) return;
        api.__formdraftHooked = true;
        try {
            // Cell edits inside AG-Grid don't always bubble usable DOM events.
            api.addEventListener('cellValueChanged', () => markDirty(true));
        } catch (_e) { /* older grid api without addEventListener */ }
    });
}

function markDirty(trusted) {
    if (!mgr) return;
    if (nowMs() <= suppressDirtyUntil) return;
    // Programmatic change events fire constantly during data loads; only count
    // edits backed by a real recent user interaction (same rule as the
    // salesdataform unsaved reminder).
    if (trusted || nowMs() - lastUserInteraction < 1000) {
        dirty = true;
    }
}

function scanForDataform() {
    const ctx = findSaveContext();
    if (!ctx) {
        mgr = null;
        return;
    }
    const scope = findFormScope(ctx.slug, ctx.btn);
    const recordId = currentRecordId(ctx.slug, scope);
    if (!mgr || mgr.slug !== ctx.slug || mgr.recordId !== recordId) {
        mgr = { slug: ctx.slug, scope, recordId, key: draftKey(ctx.slug, recordId), instanceId: newFormInstanceId() };
        dirty = false;
    } else {
        mgr.scope = scope;
    }
    hookGridEdits(scope);
    maybeShowBanner();
    if (!snapshotTimer) {
        snapshotTimer = setInterval(flushIfDirty, SNAPSHOT_INTERVAL_MS);
    }
}

function init() {
    let scanQueued = null;
    const queueScan = () => {
        clearTimeout(scanQueued);
        scanQueued = setTimeout(scanForDataform, 250);
    };

    scanForDataform();
    document.addEventListener('htmx:afterSettle', queueScan);

    ['pointerdown', 'keydown'].forEach((eventName) => {
        document.addEventListener(eventName, (evt) => {
            if (evt.isTrusted && mgr && (mgr.scope.contains(evt.target) || evt.target.closest?.('.ag-popup'))) {
                lastUserInteraction = nowMs();
            }
        }, true);
    });
    ['input', 'change'].forEach((eventName) => {
        document.addEventListener(eventName, (evt) => {
            if (!mgr) return;
            const target = evt.target;
            if (!target || !(mgr.scope.contains(target) || target.closest?.('.ag-popup'))) return;
            markDirty(evt.isTrusted);
        }, true);
    });

    // The form can die at any moment (close, crash, navigation, freeze →
    // refresh): flush the pending edits whenever the page may be going away.
    window.addEventListener('pagehide', flushIfDirty);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushIfDirty();
    });

    // Snapshot at the exact moment of every save attempt: if the save fails in
    // any way (network, 500, timeout), the draft holds what the user submitted.
    document.addEventListener('htmx:beforeRequest', (evt) => {
        const path = evt.detail?.requestConfig?.path || '';
        if (!SAVE_PATH_RE.test(path) || !mgr) return;
        dirty = false;
        captureNow();
        pendingSaveKey = mgr.key;
    });

    document.addEventListener('htmx:afterRequest', (evt) => {
        const path = evt.detail?.requestConfig?.path || '';
        if (!SAVE_PATH_RE.test(path)) return;
        const xhr = evt.detail?.xhr;
        if (evt.detail?.successful && xhr && SUCCESS_FLASH_RE.test(xhr.responseText || '')) {
            if (pendingSaveKey) removeDraft(pendingSaveKey);
            if (mgr) removeDraft(mgr.key);
            pendingSaveKey = null;
            dirty = false;
            // Post-save OOB swaps fire change events; don't let them re-dirty.
            suppressDirtyUntil = nowMs() + 2000;
            removeBanner();
        }
    });

    // A hung/failed SAVE used to die silently while the save button sat
    // disabled — tell the user the entry is safe. Gated to save requests on an
    // active dataform so this globally-imported handler stays quiet for the
    // app's many other POSTs (which have their own error handling).
    ['htmx:timeout', 'htmx:sendError'].forEach((eventName) => {
        document.addEventListener(eventName, (evt) => {
            const config = evt.detail?.requestConfig;
            if (!config || !SAVE_PATH_RE.test(config.path || '')) return;
            if (!mgr && !pendingSaveKey) return;
            if (nowMs() - lastNetToastAt < 3000) return;
            lastNetToastAt = nowMs();
            showFlashMessage('formdraft-network', 'warning',
                'The save did not go through (network or server issue). Your entry is still on this page'
                + ' and a backup copy was kept on this device — please try again.', 10);
        });
    });

    // Stuck-loader watchdog. base.html shows #global-page-loader on every
    // beforeunload, but downloads and cancelled navigations fire beforeunload
    // without unloading the page — the overlay then covers the form and
    // silently blocks every click ("Save stopped working"). If this JS context
    // is still alive 8s after a beforeunload, the navigation never happened, so
    // hide the loader. A real unload kills this timer with the page.
    window.addEventListener('beforeunload', () => {
        setTimeout(() => {
            const loader = document.getElementById('global-page-loader');
            if (loader) loader.classList.add('hidden');
        }, 8000);
    });

    pruneDrafts(false, null);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
